// Cliente HTTP autenticado contra el gateway.
// Todas las rutas pasan por /api/* (Worker o nginx hacen el resto).
//
// Auto-refresh: si estamos en modo Keycloak, antes de CADA request pedimos
// a keycloak-js renovar el token si le quedan <30s. Esto elimina los 401
// por tokens vencidos sin requerir refresh manual del usuario.

import { session, logout, refreshPilotSession } from './auth.svelte';
import { isKeycloakMode, refreshIfNeeded } from './keycloak';
import { isPilotMode } from './pilot';

type Body = Record<string, unknown> | unknown[] | undefined;

function redirectTrialExpired(): void {
  if (typeof location !== 'undefined' && !location.pathname.startsWith('/billing/trial-expired')) {
    location.href = '/billing/trial-expired';
  }
}

async function request<T>(method: string, path: string, body?: Body): Promise<T> {
  // 1. Renovar token si estamos cerca del expiry (keycloak only).
  if (isKeycloakMode()) {
    await refreshIfNeeded(30);
  }

  const s = session.value;
  const headers: Record<string, string> = { 'Accept': 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (s) {
    headers['Authorization'] = `Bearer ${s.token}`;
    headers['X-Tenant-Id'] = s.tenantId;
  }

  let r = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined
  });

  // 2. Si igual recibimos 401, intentar un único refresh forzado y reintentar
  if (r.status === 401 && isPilotMode()) {
    const ps = await refreshPilotSession();
    if (ps) {
      headers['Authorization'] = `Bearer ${ps.token}`;
      headers['X-Tenant-Id'] = ps.tenantId;
      r = await fetch(path, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined
      });
    }
  } else if (r.status === 401 && isKeycloakMode()) {
    const newToken = await refreshIfNeeded(0);
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      r = await fetch(path, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined
      });
    }
  }

  if (r.status === 401) {
    if (!isPilotMode()) await logout();
    throw new Error('Sesión expirada — vuelve a iniciar sesión.');
  }
  if (r.status === 402) {
    redirectTrialExpired();
    throw new Error('Periodo de prueba vencido — active su plan para continuar.');
  }
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(`${r.status} ${r.statusText}${txt ? ' — ' + txt.slice(0, 300) : ''}`);
  }
  if (r.status === 204) return undefined as T;
  const ct = r.headers.get('content-type') ?? '';
  return (ct.includes('application/json') ? await r.json() : (await r.text() as unknown)) as T;
}

export const http = {
  get:  <T = any>(p: string)                 => request<T>('GET',    p),
  post: <T = any>(p: string, body: Body)     => request<T>('POST',   p, body),
  put:  <T = any>(p: string, body: Body)     => request<T>('PUT',    p, body),
  patch:<T = any>(p: string, body: Body)     => request<T>('PATCH',  p, body),
  del:  <T = any>(p: string)                 => request<T>('DELETE', p)
};

/** Descarga binaria autenticada (PDF, etc.) vía gateway. */
export async function downloadAuthenticatedFile(path: string, filename: string): Promise<void> {
  if (isKeycloakMode()) {
    await refreshIfNeeded(30);
  }
  const s = session.value;
  const headers: Record<string, string> = {};
  if (s) {
    headers['Authorization'] = `Bearer ${s.token}`;
    headers['X-Tenant-Id'] = s.tenantId;
  }
  let r = await fetch(path, { method: 'GET', headers });
  if (r.status === 401 && isPilotMode()) {
    const ps = await refreshPilotSession();
    if (ps) {
      headers['Authorization'] = `Bearer ${ps.token}`;
      headers['X-Tenant-Id'] = ps.tenantId;
      r = await fetch(path, { method: 'GET', headers });
    }
  } else if (r.status === 401 && isKeycloakMode()) {
    const newToken = await refreshIfNeeded(0);
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      r = await fetch(path, { method: 'GET', headers });
    }
  }
  if (r.status === 401) {
    if (!isPilotMode()) await logout();
    throw new Error('Sesión expirada — vuelve a iniciar sesión.');
  }
  if (r.status === 402) {
    redirectTrialExpired();
    throw new Error('Periodo de prueba vencido — active su plan para continuar.');
  }
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(`${r.status} ${r.statusText}${txt ? ' — ' + txt.slice(0, 300) : ''}`);
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Tipos del dominio (subset usado por el SPA) ──────────────────────────────
export type Paciente = {
  id?: string;
  tenantId?: string;
  cui?: string | null;
  nombres: string;
  apellidos: string;
  fechaNacimiento?: string | null;     // ISO yyyy-mm-dd
  sexo?: 'M' | 'F' | 'O' | null;
  telefono?: string | null;
  direccion?: string | null;
  email?: string | null;
  esNn?: boolean;
  activo?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  // Ficha clínica (misma forma que HOSPITALES)
  motivoConsulta?: string | null;
  antecedentesPersonales?: string | null;
  antecedentesFamiliares?: string | null;
  antecedentesQuirurgicos?: string | null;
  alergias?: string | null;
  diagnostico?: string | null;
  habitosToxicos?: string | null;
  historiaEnfermedadActual?: string | null;
};

export type Cama = {
  id?: string;
  tenantId?: string;
  numero: string;
  servicio?: string | null;
  sala?: string | null;
  esUci?: boolean;
  // Debe coincidir con el enum del backend (camas-svc Cama.Estado). NO existe 'LIMPIEZA'.
  estado: 'DISPONIBLE' | 'OCUPADA' | 'MANTENIMIENTO' | 'AISLAMIENTO' | 'BLOQUEADA' | 'SUCIA' | 'LIMPIA' | 'ASIGNADA';
};

export type Ingreso = {
  id?: string;
  tenantId?: string;
  pacienteId: string;
  camaId: string;
  motivoIngreso?: string | null;
  diagnosticoIngreso?: string | null;
  estado?: 'ACTIVO' | 'ALTA';
  ingresadoEn?: string;
  egresadoEn?: string | null;
};

export type Nota = {
  id?: string;
  tenantId?: string;
  pacienteId: string;
  episodioId?: string | null;
  autor: string;
  contenido: string;
  /** SOAP, SBAR, RUTINA_FISIO, etc. */
  tipo?: string | null;
  registradaEn?: string;
};

export type Renglon = {
  id?: string;
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  total?: number;
};

export type Cuenta = {
  id?: string;
  tenantId?: string;
  pacienteId: string;
  aseguradora?: string | null;
  aseguradoraId?: string | null;
  deducible?: number | null;
  copago?: number | null;
  montoCubierto?: number | null;
  estado?: 'ABIERTA' | 'CERRADA';
  total?: number;
  renglones?: Renglon[];
};

// ── Endpoints agrupados por servicio ─────────────────────────────────────────
export const Pacientes = {
  list:    (q?: string, opts?: { activo?: boolean; mes?: string }) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (opts?.activo === false) params.set('activo', 'false');
    if (opts?.mes) params.set('mes', opts.mes);
    const qs = params.toString();
    return http.get<Paciente[]>('/api/admision/v1/admision/pacientes' + (qs ? `?${qs}` : ''));
  },
  uno:     (id: string)         => http.get<Paciente>(`/api/admision/v1/admision/pacientes/${id}`),
  porCui:  (cui: string)        => http.get<Paciente>(`/api/admision/v1/admision/pacientes/cui/${cui}`),
  crear:   (p: Paciente)        => http.post<Paciente>('/api/admision/v1/admision/pacientes', p),
  actualizar: (id: string, p: Paciente) => http.put<Paciente>(`/api/admision/v1/admision/pacientes/${id}`, p),
  eliminar: (id: string)        => http.del(`/api/admision/v1/admision/pacientes/${id}`),
};

export const Camas = {
  list:   ()                       => http.get<Cama[]>('/api/camas/v1/camas'),
  create: (c: Cama)                => http.post<Cama>('/api/camas/v1/camas', c),
  estado: (id: string, e: string)  => http.post<Cama>(`/api/camas/v1/camas/${id}/estado?estado=${e}`, undefined),
  seedDemo: () => http.post<{
    tenantId: string;
    pacientesInsertados: number;
    pacientesExistentes: number;
    camasInsertadas: number;
    camasExistentes: number;
    ingresosInsertados: number;
    ingresosExistentes: number;
    totalCamas: number;
    camasOcupadas: number;
    tasaOcupacionPct: number;
  }>('/api/camas/v1/camas/demo/seed', undefined),
};

export const Ingresos = {
  activos: () => http.get<Ingreso[]>('/api/camas/v1/ingresos/activos'),
  crear:   (i: Ingreso) => http.post<Ingreso>('/api/camas/v1/ingresos', i),
  alta:    (id: string) => http.post<Ingreso>(`/api/camas/v1/ingresos/${id}/alta`, undefined),
};

export const Notas = {
  porPaciente: (pid: string) => http.get<Nota[]>(`/api/expediente/v1/expediente/pacientes/${pid}/notas`),
  crear:       (n: Nota)     => http.post<Nota>('/api/expediente/v1/expediente/notas', n),
};

/** IA de consulta: estructura transcripción → borrador. No persiste. */
export type BorradorConsultaIa = {
  fromAI?: boolean;
  mensaje?: string;
  motivoConsulta?: string;
  historiaEnfermedadActual?: string;
  antecedentesPersonales?: string;
  antecedentesFamiliares?: string;
  antecedentesQuirurgicos?: string;
  habitosToxicos?: string;
  alergias?: string;
  diagnostico?: string;
  notaEvolucion?: string;
  dxDiferenciales?: string[];
  labsSugeridos?: string[];
};

export const ConsultaIa = {
  estado: () => http.get<{ enabled: boolean }>('/api/expediente/v1/expediente/ia/estado'),
  estructurar: (transcripcion: string) =>
    http.post<BorradorConsultaIa>('/api/expediente/v1/expediente/ia/estructurar', { transcripcion }),
};

export const Cuentas = {
  list:    ()                                       => http.get<Cuenta[]>('/api/facturacion/v1/facturacion/cuentas'),
  abrir:   (c: Cuenta)                              => http.post<Cuenta>('/api/facturacion/v1/facturacion/cuentas', c),
  renglon: (id: string, r: Renglon)                 => http.post<Cuenta>(`/api/facturacion/v1/facturacion/cuentas/${id}/renglones`, r),
  cerrar:  (id: string)                             => http.post<Cuenta>(`/api/facturacion/v1/facturacion/cuentas/${id}/cerrar`, undefined),
};

// ── Aseguradoras / Seguros ───────────────────────────────────────────────────
export type Aseguradora = {
  id?: string;
  codigo: string;
  nombre: string;
  nit?: string | null;
  contacto?: string | null;
  telefono?: string | null;
  email?: string | null;
  porcentajeDefault: number;
  activa?: boolean;
};
export type CoberturaResult = {
  cuentaId: string;
  total: number;
  deducible: number;
  copago: number;
  porcentajeCobertura: number;
  pagaAseguradora: number;
  pagaPaciente: number;
};
export const Seguros = {
  list:        (soloActivas = true) => http.get<Aseguradora[]>(`/api/facturacion/v1/facturacion/aseguradoras?soloActivas=${soloActivas}`),
  crear:       (a: Aseguradora)     => http.post<Aseguradora>('/api/facturacion/v1/facturacion/aseguradoras', a),
  actualizar:  (id: string, a: Aseguradora) => http.put<Aseguradora>(`/api/facturacion/v1/facturacion/aseguradoras/${id}`, a),
  autorizar:   (cuentaId: string, body: { numeroAutorizacion?: string; polizaSeguro?: string; autorizacionEstado?: string; }) =>
                 http.post<Cuenta>(`/api/facturacion/v1/facturacion/cuentas/${cuentaId}/autorizacion`, body),
  calcular:    (cuentaId: string, body: { porcentajeCobertura?: number; deducible?: number; copago?: number; aseguradoraId?: string } = {}) =>
                 http.post<CoberturaResult>(`/api/facturacion/v1/facturacion/cuentas/${cuentaId}/calcular-cobertura`, body),
};


// ── Consultorios / clínicas asociadas + profesionales ────────────────────────
export type Consultorio = {
  id?: string;
  codigo: string;
  nombre: string;
  tipo: 'PROPIO' | 'ARRENDADO';
  vinculo?: 'INTERNO' | 'EXTERNO';
  tenantExternoId?: string | null;
  medicoResponsableId?: string | null;
  modeloCobro?: 'COMISION' | 'RENTA_FIJA' | 'MIXTO' | null;
  comisionHospitalPct?: number;
  rentaMensual?: number;
  activo?: boolean;
};
export type Profesional = {
  id?: string;
  nombre: string;
  colegiado?: string | null;
  nit?: string | null;
  tipoRelacion: 'EMPLEADO' | 'INDEPENDIENTE';
  honorarioPct?: number;
  activo?: boolean;
  firmaUrl?: string | null;
  selloUrl?: string | null;
};
export type ReporteConsultorioItem = {
  consultorioId: string;
  codigo?: string | null;
  nombre: string;
  tipo?: string | null;
  renglones: number;
  totalIngresos: number;
  comisionHospitalPct: number;
  comisionHospitalEstimada: number;
  porCentroIngreso: Record<string, number>;
};
export type ReporteIngresosConsultorio = {
  desde: string;
  hasta: string;
  totalIngresos: number;
  comisionHospitalEstimada: number;
  consultorios: ReporteConsultorioItem[];
};
export type Liquidacion = {
  id?: string;
  consultorioId: string;
  consultorioNombre?: string | null;
  medicoId?: string | null;
  desde: string;
  hasta: string;
  estado?: 'BORRADOR' | 'EMITIDA' | 'PAGADA' | 'ANULADA';
  renglones?: number;
  ingresosTotales?: number;
  comisionHospital?: number;
  renta?: number;
  retencionHospital?: number;
  netoMedico?: number;
  neteoConsultorio?: number;
  netoPagar?: number;
  detalleCentros?: string | null;
  pagadaEn?: string | null;
};
const FACT = '/api/facturacion/v1/facturacion';
export const Consultorios = {
  list:          (soloActivos = true) => http.get<Consultorio[]>(`${FACT}/consultorios?soloActivos=${soloActivos}`),
  crear:         (c: Consultorio)     => http.post<Consultorio>(`${FACT}/consultorios`, c),
  actualizar:    (id: string, c: Consultorio) => http.put<Consultorio>(`${FACT}/consultorios/${id}`, c),
  profesionales: (soloActivos = true) => http.get<Profesional[]>(`${FACT}/profesionales?soloActivos=${soloActivos}`),
  crearProf:     (p: Profesional)     => http.post<Profesional>(`${FACT}/profesionales`, p),
  actualizarProf:(id: string, p: Profesional) => http.put<Profesional>(`${FACT}/profesionales/${id}`, p),
  reporteIngresos: (desde?: string, hasta?: string) =>
    http.get<ReporteIngresosConsultorio>(`${FACT}/reportes/ingresos-por-consultorio${desde && hasta ? `?desde=${desde}&hasta=${hasta}` : ''}`),
  estadoCuenta: (desde?: string, hasta?: string) =>
    http.get<ReporteEstadoCuenta>(`${FACT}/reportes/estado-cuenta-consultorio${desde && hasta ? `?desde=${desde}&hasta=${hasta}` : ''}`),
};
export type EstadoCuentaItem = {
  consultorioId: string;
  codigo?: string | null;
  nombre: string;
  renglones: number;
  totalAdeudado: number;
  porCentroIngreso: Record<string, number>;
};
export type ReporteEstadoCuenta = {
  desde: string;
  hasta: string;
  totalAdeudado: number;
  sinConsultorio: number;
  consultorios: EstadoCuentaItem[];
};
export type Tarifa = {
  id?: string;
  codigo: string;
  descripcion: string;
  centroIngreso?: string | null;
  precioPublico: number;
  precioInterno?: number | null;
  activo?: boolean;
};
export type TarifaConvenio = {
  id?: string;
  codigo: string;
  aseguradoraId: string;
  precioConvenio: number;
  activo?: boolean;
};
export const Tarifas = {
  list:       (soloActivos = true) => http.get<Tarifa[]>(`${FACT}/tarifas?soloActivos=${soloActivos}`),
  crear:      (t: Tarifa) => http.post<Tarifa>(`${FACT}/tarifas`, t),
  actualizar: (id: string, t: Tarifa) => http.put<Tarifa>(`${FACT}/tarifas/${id}`, t),
  convenios:       (aseguradoraId?: string) => http.get<TarifaConvenio[]>(`${FACT}/tarifas/convenios${aseguradoraId ? `?aseguradoraId=${aseguradoraId}` : ''}`),
  crearConvenio:   (tc: TarifaConvenio) => http.post<TarifaConvenio>(`${FACT}/tarifas/convenios`, tc),
  actualizarConvenio: (id: string, tc: TarifaConvenio) => http.put<TarifaConvenio>(`${FACT}/tarifas/convenios/${id}`, tc),
};
export type CuentaConsultorio = {
  id: string;
  consultorioId: string;
  consultorioNombre?: string | null;
  totalCargos: number;
  totalAbonos: number;
  saldo: number;
  estado: 'ABIERTA' | 'SALDADA';
};
export type MovimientoConsultorio = {
  id: string;
  cuentaConsultorioId: string;
  tipo: 'CARGO' | 'ABONO';
  monto: number;
  descripcion?: string | null;
  referencia?: string | null;
  createdAt?: string;
};
export const CuentasConsultorio = {
  list:        () => http.get<CuentaConsultorio[]>(`${FACT}/cuentas-consultorio`),
  movimientos: (cuentaId: string) => http.get<MovimientoConsultorio[]>(`${FACT}/cuentas-consultorio/${cuentaId}/movimientos`),
  abonar:      (consultorioId: string, body: { monto: number; descripcion?: string; referencia?: string }) =>
                 http.post<CuentaConsultorio>(`${FACT}/cuentas-consultorio/${consultorioId}/abonos`, body),
};
export const Liquidaciones = {
  generar: (desde: string, hasta: string, consultorioId?: string) =>
    http.post<Liquidacion[]>(`${FACT}/liquidaciones/generar?desde=${desde}&hasta=${hasta}${consultorioId ? `&consultorioId=${consultorioId}` : ''}`, undefined),
  list:    (consultorioId?: string) => http.get<Liquidacion[]>(`${FACT}/liquidaciones${consultorioId ? `?consultorioId=${consultorioId}` : ''}`),
  emitir:  (id: string) => http.post<Liquidacion>(`${FACT}/liquidaciones/${id}/emitir`, undefined),
  pagar:   (id: string) => http.post<Liquidacion>(`${FACT}/liquidaciones/${id}/pagar`, undefined),
  anular:  (id: string) => http.post<Liquidacion>(`${FACT}/liquidaciones/${id}/anular`, undefined),
};
// Modelo B · acuerdo bilateral hospital ↔ clínica externa (slice B1)
export type AcuerdoClinica = {
  id: string;
  tenantId?: string;
  clinicaTenantId: string;
  consultorioId: string;
  clinicaNombre?: string | null;
  estado: 'PROPUESTO' | 'ACEPTADO' | 'SUSPENDIDO' | 'TERMINADO';
  comparteResultadosLab: boolean;
  comparteResultadosImagen: boolean;
  comparteEstadoCuenta: boolean;
  aceptadoEn?: string | null;
  notas?: string | null;
  createdAt?: string;
};
export type EstadoCuentaCompartido = {
  hospitalTenantId: string;
  consultorioId: string;
  clinicaNombre?: string | null;
  totalCargos: number;
  totalAbonos: number;
  saldo: number;
  estadoCuenta: string;
};
export const Compartido = {
  // Lado clínica: estado de cuenta con hospitales aliados (acuerdo ACEPTADO)
  estadoCuenta: () => http.get<EstadoCuentaCompartido[]>(`${FACT}/compartido/estado-cuenta`),
  movimientos:  (consultorioId: string) => http.get<MovimientoConsultorio[]>(`${FACT}/compartido/estado-cuenta/${consultorioId}/movimientos`),
};
export const AcuerdosClinica = {
  // Lado hospital
  list:      () => http.get<AcuerdoClinica[]>(`${FACT}/acuerdos-clinica`),
  proponer:  (body: { consultorioId: string; comparteResultadosLab?: boolean; comparteResultadosImagen?: boolean; comparteEstadoCuenta?: boolean; notas?: string }) =>
               http.post<AcuerdoClinica>(`${FACT}/acuerdos-clinica`, body),
  suspender: (id: string) => http.post<AcuerdoClinica>(`${FACT}/acuerdos-clinica/${id}/suspender`, undefined),
  terminar:  (id: string) => http.post<AcuerdoClinica>(`${FACT}/acuerdos-clinica/${id}/terminar`, undefined),
  // Lado clínica
  entrantes: () => http.get<AcuerdoClinica[]>(`${FACT}/acuerdos-clinica/entrantes`),
  aceptar:   (id: string) => http.post<AcuerdoClinica>(`${FACT}/acuerdos-clinica/entrantes/${id}/aceptar`, undefined),
  rechazar:  (id: string) => http.post<AcuerdoClinica>(`${FACT}/acuerdos-clinica/entrantes/${id}/rechazar`, undefined),
};

// ── Quirófano ────────────────────────────────────────────────────────────────
export type Sala = {
  id?: string;
  codigo: string;
  nombre?: string | null;
  estado?: 'DISPONIBLE' | 'OCUPADA' | 'LIMPIEZA' | 'MANTENIMIENTO';
};
export type Cirugia = {
  id?: string;
  pacienteId: string;
  salaId: string;
  episodioId?: string | null;
  procedimiento: string;
  cirujanoId?: string | null;
  fechaProgramada: string;          // ISO con offset
  duracionEstimadaMin?: number | null;
  estado?: 'PROGRAMADA' | 'EN_CURSO' | 'FINALIZADA' | 'CANCELADA';
};
export const Quirofano = {
  salas:      ()              => http.get<Sala[]>('/api/quirofano/v1/quirofano/salas'),
  crearSala:  (s: Sala)       => http.post<Sala>('/api/quirofano/v1/quirofano/salas', s),
  agenda:     (desde: string, hasta: string) =>
    http.get<Cirugia[]>(`/api/quirofano/v1/quirofano/agenda?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`),
  programar:  (c: Cirugia)    => http.post<Cirugia>('/api/quirofano/v1/quirofano/cirugias', c),
};

// ── Farmacia hospitalaria ────────────────────────────────────────────────────
export type Medicamento = {
  id?: string;
  codigo: string;
  nombre: string;
  presentacion?: string | null;
  principioActivo?: string | null;
  requiereReceta?: boolean;
  esControlado?: boolean;
};
export type Lote = {
  id?: string;
  medicamentoId: string;
  bodega?: string | null;
  numeroLote: string;
  vencimiento: string;            // yyyy-mm-dd
  cantidad: number;
};
export const Farmacia = {
  meds:        ()                          => http.get<Medicamento[]>('/api/farmacia-hosp/v1/farmacia-hosp/medicamentos'),
  crearMed:    (m: Medicamento)            => http.post<Medicamento>('/api/farmacia-hosp/v1/farmacia-hosp/medicamentos', m),
  crearLote:   (l: Lote)                   => http.post<Lote>('/api/farmacia-hosp/v1/farmacia-hosp/lotes', l),
  disponibles: (medicamentoId: string)     => http.get<Lote[]>(`/api/farmacia-hosp/v1/farmacia-hosp/lotes/disponibles?medicamentoId=${medicamentoId}`),
  dispensar:   (body: { medicamentoId: string; pacienteId: string; cantidad: number; dispensadoPor?: string }) =>
    http.post<any>('/api/farmacia-hosp/v1/farmacia-hosp/dispensar', body),
};

// ── Laboratorio (LIS) ────────────────────────────────────────────────────────
export type ExamenCatalogo = {
  id?: string;
  codigo: string;
  nombre: string;
  categoria: 'HEMATOLOGIA' | 'QUIMICA_SANGUINEA' | 'ORINA' | 'HECES' | 'INMUNOLOGIA' | 'MICROBIOLOGIA' | 'HORMONAS' | 'COAGULACION' | 'OTROS';
  unidad?: string | null;
  valorRefMin?: number | null;
  valorRefMax?: number | null;
  valorRefTexto?: string | null;
  metodo?: string | null;
  precio?: number;
  moneda?: string;
  tiempoEntregaHoras?: number | null;
  activo?: boolean;
};
export type OrdenExamen = {
  id?: string;
  ordenId?: string;
  examenCodigo: string;
  examenNombre: string;
  categoria?: string | null;
  unidad?: string | null;
  valorRefMin?: number | null;
  valorRefMax?: number | null;
  valorRefTexto?: string | null;
  precio?: number;
  valor?: string | null;
  flag?: 'NORMAL' | 'ALTO' | 'BAJO' | 'CRITICO' | 'SIN_RANGO' | null;
  metodo?: string | null;
};
export type OrdenLab = {
  id?: string;
  numero?: string;
  pacienteId: string;
  pacienteNombre?: string;
  episodioId?: string | null;
  medicoSolicitante?: string | null;
  prioridad?: 'RUTINA' | 'URGENTE';
  estado?: 'SOLICITADA' | 'EN_PROCESO' | 'RESULTADOS_LISTOS' | 'VALIDADA' | 'ENTREGADA' | 'ANULADA';
  observaciones?: string | null;
  total?: number;
  moneda?: string;
  codigoValidacion?: string | null;
  validadaPor?: string | null;
  cuentaId?: string | null;
  facturadoEn?: string | null;
};
export type OrdenLabDetalle = { orden: OrdenLab; examenes: OrdenExamen[] };
export type CrearOrdenLab = {
  pacienteId: string;
  pacienteNombre?: string;
  medicoSolicitante?: string;
  consultorioId?: string;
  medicoSolicitanteId?: string;
  pagador?: 'PACIENTE' | 'CONSULTORIO' | 'ASEGURADORA';
  prioridad?: 'RUTINA' | 'URGENTE';
  observaciones?: string;
  examenes: string[];
};
const LAB = '/api/laboratorio/v1/laboratorio';
export const Laboratorio = {
  catalogo:    (todos = false)            => http.get<ExamenCatalogo[]>(`${LAB}/catalogo${todos ? '?todos=true' : ''}`),
  crearExamen: (e: ExamenCatalogo)        => http.post<ExamenCatalogo>(`${LAB}/catalogo`, e),
  actualizarExamen: (codigo: string, e: Partial<ExamenCatalogo>) =>
                 http.put<ExamenCatalogo>(`${LAB}/catalogo/${encodeURIComponent(codigo)}`, e),
  seed:        ()                         => http.post<{ insertados: number; existentes: number; total: number }>(`${LAB}/catalogo/seed`, undefined),
  ordenes:     (estado?: string)          => http.get<OrdenLab[]>(`${LAB}/ordenes${estado ? `?estado=${estado}` : ''}`),
  orden:       (id: string)               => http.get<OrdenLabDetalle>(`${LAB}/ordenes/${id}`),
  crearOrden:  (o: CrearOrdenLab)         => http.post<OrdenLabDetalle>(`${LAB}/ordenes`, o),
  resultado:   (id: string, renglonId: string, body: { valor: string; metodo?: string; resultadoPor?: string }) =>
                 http.post<OrdenLabDetalle>(`${LAB}/ordenes/${id}/examenes/${renglonId}/resultado`, body),
  validar:     (id: string, validadaPor?: string) => http.post<OrdenLabDetalle>(`${LAB}/ordenes/${id}/validar`, { validadaPor }),
  entregar:    (id: string)               => http.post<OrdenLabDetalle>(`${LAB}/ordenes/${id}/entregar`, undefined),
  notificar:   (id: string, body: { canales: string[]; email?: string; telefono?: string }) =>
                 http.post<any[]>(`${LAB}/ordenes/${id}/notificaciones`, body),
  reporte:     (id: string)               => http.get<string>(`${LAB}/ordenes/${id}/reporte`),
  // B2 · lado clínica: resultados compartidos por hospitales aliados (acuerdo ACEPTADO)
  compartidoOrdenes: ()                   => http.get<OrdenLab[]>(`${LAB}/compartido/ordenes`),
  compartidoOrden:   (id: string)         => http.get<OrdenLabDetalle>(`${LAB}/compartido/ordenes/${id}`),
};

// ── Imagenología (RIS) ───────────────────────────────────────────────────────
export type EstudioCatalogo = {
  id?: string;
  codigo: string;
  nombre: string;
  modalidad: 'RAYOS_X' | 'ULTRASONIDO' | 'TOMOGRAFIA' | 'RESONANCIA' | 'MAMOGRAFIA' | 'FLUOROSCOPIA' | 'DENSITOMETRIA' | 'OTROS';
  region?: string | null;
  preparacion?: string | null;
  precio?: number;
  moneda?: string;
  tiempoEntregaHoras?: number | null;
  activo?: boolean;
};
export type OrdenEstudio = {
  id?: string;
  ordenId?: string;
  estudioCodigo: string;
  estudioNombre: string;
  modalidad?: string | null;
  region?: string | null;
  precio?: number;
};
export type OrdenImagen = {
  id?: string;
  numero?: string;
  pacienteId: string;
  pacienteNombre?: string;
  episodioId?: string | null;
  medicoSolicitante?: string | null;
  indicacionClinica?: string | null;
  prioridad?: 'RUTINA' | 'URGENTE';
  estado?: 'SOLICITADA' | 'EN_ADQUISICION' | 'ADQUIRIDA' | 'INFORMADA' | 'ENTREGADA' | 'ANULADA';
  tecnica?: string | null;
  hallazgos?: string | null;
  impresion?: string | null;
  recomendaciones?: string | null;
  imagenesUrl?: string | null;
  radiologo?: string | null;
  total?: number;
  moneda?: string;
  codigoValidacion?: string | null;
  cuentaId?: string | null;
};
export type OrdenImagenDetalle = { orden: OrdenImagen; estudios: OrdenEstudio[] };
export type CrearOrdenImagen = {
  pacienteId: string;
  pacienteNombre?: string;
  medicoSolicitante?: string;
  consultorioId?: string;
  medicoSolicitanteId?: string;
  pagador?: 'PACIENTE' | 'CONSULTORIO' | 'ASEGURADORA';
  indicacionClinica?: string;
  prioridad?: 'RUTINA' | 'URGENTE';
  estudios: string[];
};
export type InformarImagen = {
  tecnica?: string;
  hallazgos: string;
  impresion: string;
  recomendaciones?: string;
  imagenesUrl?: string;
  radiologo?: string;
};
const IMG = '/api/imagenologia/v1/imagenologia';
export const Imagenologia = {
  catalogo:    (todos = false)            => http.get<EstudioCatalogo[]>(`${IMG}/catalogo${todos ? '?todos=true' : ''}`),
  crearEstudio:(e: EstudioCatalogo)       => http.post<EstudioCatalogo>(`${IMG}/catalogo`, e),
  actualizarEstudio: (codigo: string, e: Partial<EstudioCatalogo>) =>
                 http.put<EstudioCatalogo>(`${IMG}/catalogo/${encodeURIComponent(codigo)}`, e),
  seed:        ()                         => http.post<{ insertados: number; existentes: number; total: number }>(`${IMG}/catalogo/seed`, undefined),
  ordenes:     (estado?: string)          => http.get<OrdenImagen[]>(`${IMG}/ordenes${estado ? `?estado=${estado}` : ''}`),
  orden:       (id: string)               => http.get<OrdenImagenDetalle>(`${IMG}/ordenes/${id}`),
  crearOrden:  (o: CrearOrdenImagen)      => http.post<OrdenImagenDetalle>(`${IMG}/ordenes`, o),
  adquirir:    (id: string, imagenesUrl?: string) => http.post<OrdenImagenDetalle>(`${IMG}/ordenes/${id}/adquirir`, { imagenesUrl }),
  informar:    (id: string, body: InformarImagen) => http.post<OrdenImagenDetalle>(`${IMG}/ordenes/${id}/informar`, body),
  entregar:    (id: string)               => http.post<OrdenImagenDetalle>(`${IMG}/ordenes/${id}/entregar`, undefined),
  informe:     (id: string)               => http.get<string>(`${IMG}/ordenes/${id}/informe`),
  // B2 · lado clínica: informes compartidos por hospitales aliados (acuerdo ACEPTADO)
  compartidoOrdenes: ()                   => http.get<OrdenImagen[]>(`${IMG}/compartido/ordenes`),
  compartidoOrden:   (id: string)         => http.get<OrdenImagenDetalle>(`${IMG}/compartido/ordenes/${id}`),
};

// ── Veterinaria (mascotas, internamiento, mostrador/farmacia) ────────────────
export type Propietario = {
  id?: string;
  tenantId?: string;
  nombre: string;
  telefono?: string | null;
  email?: string | null;
  documento?: string | null;
  direccion?: string | null;
  notas?: string | null;
};
export type Mascota = {
  id?: string;
  tenantId?: string;
  propietarioId: string;
  nombre: string;
  especie: 'PERRO' | 'GATO' | 'AVE' | 'CONEJO' | 'ROEDOR' | 'REPTIL' | 'EQUINO' | 'OTRO';
  raza?: string | null;
  sexo: 'MACHO' | 'HEMBRA' | 'DESCONOCIDO';
  fechaNacimiento?: string | null;
  color?: string | null;
  microchip?: string | null;
  esterilizado?: boolean;
  pesoActual?: number | null;
  alergias?: string | null;
  activo?: boolean;
};
export type NotaExpedienteVet = {
  id?: string;
  mascotaId?: string;
  autor?: string | null;
  contenido: string;
  tipo?: 'EVOLUCION' | 'ANAMNESIS' | 'PROCEDIMIENTO' | 'ALTA';
  createdAt?: string;
};
export type Vacuna = {
  id?: string;
  mascotaId?: string;
  nombre: string;
  fechaAplicacion: string;
  proximaDosis?: string | null;
  lote?: string | null;
  veterinario?: string | null;
  notas?: string | null;
};
export type PesoRegistro = {
  id?: string;
  mascotaId?: string;
  fecha: string;
  pesoKg: number;
  nota?: string | null;
};
export type Jaula = {
  id?: string;
  numero: string;
  area?: string | null;
  tamano: 'PEQUENA' | 'MEDIANA' | 'GRANDE' | 'JAULON';
  estado: 'DISPONIBLE' | 'OCUPADA' | 'MANTENIMIENTO' | 'AISLAMIENTO' | 'LIMPIA' | 'SUCIA';
};
export type InternamientoVet = {
  id?: string;
  mascotaId: string;
  jaulaId: string;
  estado?: 'ACTIVO' | 'ALTA' | 'FALLECIDO' | 'TRASLADO';
  ingresadoEn?: string;
  egresadoEn?: string | null;
  motivo?: string | null;
  veterinario?: string | null;
  observaciones?: string | null;
};
export type ProductoVet = {
  id?: string;
  codigo: string;
  nombre: string;
  categoria: 'ALIMENTO' | 'MEDICAMENTO' | 'ACCESORIO' | 'SERVICIO' | 'OTRO';
  precio: number;
  stock: number;
  unidad?: string | null;
  soloPacientes?: boolean;
  activo?: boolean;
};
export type VentaVetItem = {
  productoId?: string;
  descripcion?: string;
  cantidad: number;
  precioUnitario?: number;
};
export type CrearVentaVet = {
  propietarioId?: string;
  mascotaId?: string;
  formaPago?: 'EFECTIVO' | 'TARJETA' | 'CUENTA' | 'TRANSFERENCIA';
  notas?: string;
  items: VentaVetItem[];
};
export type VentaVet = {
  id?: string;
  propietarioId?: string | null;
  mascotaId?: string | null;
  fecha?: string;
  total?: number;
  estado?: 'ABIERTA' | 'PAGADA' | 'ANULADA';
  formaPago?: string | null;
  cuentaId?: string | null;
  notas?: string | null;
};
const VET = '/api/veterinaria/v1/veterinaria';
export const Veterinaria = {
  // Propietarios (dueños)
  propietarios:       (q?: string) => http.get<Propietario[]>(`${VET}/propietarios${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  crearPropietario:   (p: Propietario) => http.post<Propietario>(`${VET}/propietarios`, p),
  actualizarPropietario: (id: string, p: Propietario) => http.put<Propietario>(`${VET}/propietarios/${id}`, p),
  mascotasDe:         (id: string) => http.get<Mascota[]>(`${VET}/propietarios/${id}/mascotas`),
  // Mascotas + carné de vacunas + peso histórico
  mascotas:           (q?: string) => http.get<Mascota[]>(`${VET}/mascotas${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  mascota:            (id: string) => http.get<Mascota>(`${VET}/mascotas/${id}`),
  crearMascota:       (m: Mascota) => http.post<Mascota>(`${VET}/mascotas`, m),
  actualizarMascota:  (id: string, m: Mascota) => http.put<Mascota>(`${VET}/mascotas/${id}`, m),
  vacunas:            (mascotaId: string) => http.get<Vacuna[]>(`${VET}/mascotas/${mascotaId}/vacunas`),
  agregarVacuna:      (mascotaId: string, v: Vacuna) => http.post<Vacuna>(`${VET}/mascotas/${mascotaId}/vacunas`, v),
  pesos:              (mascotaId: string) => http.get<PesoRegistro[]>(`${VET}/mascotas/${mascotaId}/pesos`),
  registrarPeso:      (mascotaId: string, p: PesoRegistro) => http.post<PesoRegistro>(`${VET}/mascotas/${mascotaId}/pesos`, p),
  notasExpediente:    (mascotaId: string) => http.get<NotaExpedienteVet[]>(`${VET}/mascotas/${mascotaId}/notas`),
  agregarNotaExpediente: (mascotaId: string, n: NotaExpedienteVet) =>
    http.post<NotaExpedienteVet>(`${VET}/mascotas/${mascotaId}/notas`, n),
  // Internamiento (jaulas + hospitalización)
  jaulas:             (estado?: string) => http.get<Jaula[]>(`${VET}/jaulas${estado ? `?estado=${estado}` : ''}`),
  crearJaula:         (j: Jaula) => http.post<Jaula>(`${VET}/jaulas`, j),
  jaulaEstado:        (id: string, estado: string) => http.post<Jaula>(`${VET}/jaulas/${id}/estado?estado=${estado}`, undefined),
  internamientosActivos: () => http.get<InternamientoVet[]>(`${VET}/internamientos/activos`),
  internamientosDe:   (mascotaId: string) => http.get<InternamientoVet[]>(`${VET}/internamientos?mascotaId=${mascotaId}`),
  internar:           (i: InternamientoVet) => http.post<InternamientoVet>(`${VET}/internamientos`, i),
  altaInternamiento:  (id: string) => http.post<InternamientoVet>(`${VET}/internamientos/${id}/alta`, undefined),
  // Mostrador / farmacia
  productos:          (categoria?: string) => http.get<ProductoVet[]>(`${VET}/productos${categoria ? `?categoria=${categoria}` : ''}`),
  crearProducto:      (p: ProductoVet) => http.post<ProductoVet>(`${VET}/productos`, p),
  actualizarProducto: (id: string, p: ProductoVet) => http.put<ProductoVet>(`${VET}/productos/${id}`, p),
  ventas:             (propietarioId?: string) => http.get<VentaVet[]>(`${VET}/ventas${propietarioId ? `?propietarioId=${propietarioId}` : ''}`),
  venta:              (id: string) => http.get<{ venta: VentaVet; items: any[] }>(`${VET}/ventas/${id}`),
  crearVenta:         (v: CrearVentaVet) => http.post<{ venta: VentaVet; items: any[] }>(`${VET}/ventas`, v),
};

// ── Óptica (optometría + taller de lentes + inventario con código de barras) ─
export type PacienteOptica = {
  id?: string;
  nombre: string;
  documento?: string | null;
  telefono?: string | null;
  email?: string | null;
  fechaNacimiento?: string | null;
  sexo?: 'MASCULINO' | 'FEMENINO' | 'OTRO' | null;
  ocupacion?: string | null;
  direccion?: string | null;
  notas?: string | null;
  activo?: boolean;
};
export type ExamenOptometrico = {
  id?: string;
  pacienteId?: string;
  fecha?: string;
  optometrista?: string | null;
  motivo?: string | null;
  antecedentes?: string | null;
  usaLentes?: boolean;
  avScOd?: string | null; avScOi?: string | null;
  avCcOd?: string | null; avCcOi?: string | null;
  odEsfera?: number | null; odCilindro?: number | null; odEje?: number | null; odAdd?: number | null; odPrisma?: string | null; odAv?: string | null;
  oiEsfera?: number | null; oiCilindro?: number | null; oiEje?: number | null; oiAdd?: number | null; oiPrisma?: string | null; oiAv?: string | null;
  dipLejos?: number | null; dipCerca?: number | null;
  queratometriaOd?: string | null; queratometriaOi?: string | null;
  retinoscopiaOd?: string | null; retinoscopiaOi?: string | null;
  pioOd?: number | null; pioOi?: number | null;
  diagnostico?: string | null;
  tipoLente?: 'MONOFOCAL' | 'BIFOCAL' | 'PROGRESIVO' | 'OCUPACIONAL' | 'CONTACTO' | null;
  tratamientos?: string | null;
  recomendaciones?: string | null;
};
export type OrdenTrabajoEstado = 'RECIBIDA' | 'EN_LABORATORIO' | 'LISTA' | 'ENTREGADA' | 'CANCELADA';
export type OrdenTrabajo = {
  id?: string;
  numero?: number;
  pacienteId: string;
  examenId?: string | null;
  estado?: OrdenTrabajoEstado;
  fecha?: string;
  fechaPromesa?: string | null;
  fechaEntrega?: string | null;
  armazonMarca?: string | null; armazonModelo?: string | null; armazonColor?: string | null;
  micaMaterial?: 'ORGANICO' | 'POLICARBONATO' | 'ALTO_INDICE' | 'CRISTAL' | null;
  micaDiseno?: 'MONOFOCAL' | 'BIFOCAL' | 'PROGRESIVO' | 'OCUPACIONAL' | null;
  tratamientos?: string | null;
  gOdEsfera?: number | null; gOdCilindro?: number | null; gOdEje?: number | null; gOdAdd?: number | null;
  gOiEsfera?: number | null; gOiCilindro?: number | null; gOiEje?: number | null; gOiAdd?: number | null;
  dip?: number | null;
  laboratorioExterno?: string | null;
  costoArmazon?: number; costoMicas?: number; otros?: number;
  total?: number; anticipo?: number; saldo?: number;
  notas?: string | null;
};
export type ArticuloOptica = {
  id?: string;
  codigo: string;
  codigoBarras?: string | null;
  nombre: string;
  categoria: 'ARMAZON' | 'MICA' | 'LENTE_CONTACTO' | 'ACCESORIO' | 'LIQUIDO' | 'SERVICIO';
  marca?: string | null; modelo?: string | null; color?: string | null; material?: string | null;
  precio?: number; costo?: number;
  stock?: number; stockMinimo?: number;
  unidad?: string | null; ubicacion?: string | null;
  activo?: boolean;
};
const OPT = '/api/optica/v1/optica';
export const Optica = {
  // Pacientes
  pacientes:          (q?: string) => http.get<PacienteOptica[]>(`${OPT}/pacientes${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  paciente:           (id: string) => http.get<PacienteOptica>(`${OPT}/pacientes/${id}`),
  crearPaciente:      (p: PacienteOptica) => http.post<PacienteOptica>(`${OPT}/pacientes`, p),
  actualizarPaciente: (id: string, p: PacienteOptica) => http.put<PacienteOptica>(`${OPT}/pacientes/${id}`, p),
  // Exámenes optométricos
  examenesDe:         (pacienteId: string) => http.get<ExamenOptometrico[]>(`${OPT}/pacientes/${pacienteId}/examenes`),
  crearExamen:        (pacienteId: string, e: ExamenOptometrico) => http.post<ExamenOptometrico>(`${OPT}/pacientes/${pacienteId}/examenes`, e),
  examen:             (id: string) => http.get<ExamenOptometrico>(`${OPT}/examenes/${id}`),
  actualizarExamen:   (id: string, e: ExamenOptometrico) => http.put<ExamenOptometrico>(`${OPT}/examenes/${id}`, e),
  receta:             (id: string) => http.get<string>(`${OPT}/examenes/${id}/receta`),
  // Órdenes de taller
  ordenes:            (estado?: OrdenTrabajoEstado) => http.get<OrdenTrabajo[]>(`${OPT}/ordenes${estado ? `?estado=${estado}` : ''}`),
  ordenesDe:          (pacienteId: string) => http.get<OrdenTrabajo[]>(`${OPT}/pacientes/${pacienteId}/ordenes`),
  orden:              (id: string) => http.get<OrdenTrabajo>(`${OPT}/ordenes/${id}`),
  crearOrden:         (o: OrdenTrabajo) => http.post<OrdenTrabajo>(`${OPT}/ordenes`, o),
  actualizarOrden:    (id: string, o: OrdenTrabajo) => http.put<OrdenTrabajo>(`${OPT}/ordenes/${id}`, o),
  cambiarEstadoOrden: (id: string, estado: OrdenTrabajoEstado) => http.post<OrdenTrabajo>(`${OPT}/ordenes/${id}/estado?estado=${estado}`, undefined),
  ordenImprimir:      (id: string) => http.get<string>(`${OPT}/ordenes/${id}/imprimir`),
  // Inventario
  articulos:          (categoria?: ArticuloOptica['categoria']) => http.get<ArticuloOptica[]>(`${OPT}/articulos${categoria ? `?categoria=${categoria}` : ''}`),
  articulo:           (id: string) => http.get<ArticuloOptica>(`${OPT}/articulos/${id}`),
  articuloPorBarras:  (codigo: string) => http.get<ArticuloOptica>(`${OPT}/articulos/barras/${encodeURIComponent(codigo)}`),
  crearArticulo:      (a: ArticuloOptica) => http.post<ArticuloOptica>(`${OPT}/articulos`, a),
  actualizarArticulo: (id: string, a: ArticuloOptica) => http.put<ArticuloOptica>(`${OPT}/articulos/${id}`, a),
  ajustarStock:       (id: string, delta: number) => http.post<ArticuloOptica>(`${OPT}/articulos/${id}/stock?delta=${delta}`, undefined),
  // Calculadora de transposición / equivalente esférico
  transposicion:      (esfera: number, cilindro: number, eje: number) =>
                        http.get<{ entrada: any; transpuesto: { esfera: number; cilindro: number; eje: number }; equivalenteEsferico: number }>(
                          `${OPT}/calculadora/transposicion?esfera=${esfera}&cilindro=${cilindro}&eje=${eje}`),
};

// ── Agenda de citas (común a los 7 rubros) + recordatorios WhatsApp ──────────
export type CitaEstado = 'PROGRAMADA' | 'CONFIRMADA' | 'ATENDIDA' | 'CANCELADA' | 'NO_ASISTIO';
export type Cita = {
  id?: string;
  pacienteId?: string | null;
  pacienteNombre: string;
  telefono?: string | null;
  profesional?: string | null;
  consultorio?: string | null;
  motivo?: string | null;
  fechaHora: string;               // ISO con offset
  duracionMin?: number;
  estado?: CitaEstado;
  notas?: string | null;
  recordatorioEnviado?: boolean;
  recordatorioEn?: string | null;
  recordatorioMatutinoEnviado?: boolean;
  recordatorioMatutinoEn?: string | null;
};
const CIT = '/api/citas/v1/citas';
export const Citas = {
  /** Agenda completa o de un día (YYYY-MM-DD). */
  listar:        (dia?: string) => http.get<Cita[]>(`${CIT}${dia ? `?dia=${dia}` : ''}`),
  obtener:       (id: string) => http.get<Cita>(`${CIT}/${id}`),
  porPaciente:   (pacienteId: string) => http.get<Cita[]>(`${CIT}/paciente/${pacienteId}`),
  crear:         (c: Cita) => http.post<Cita>(CIT, c),
  actualizar:    (id: string, c: Cita) => http.put<Cita>(`${CIT}/${id}`, c),
  cambiarEstado: (id: string, estado: CitaEstado) => http.post<Cita>(`${CIT}/${id}/estado`, { estado }),
  recordar:      (id: string) => http.post<{ enviado: boolean; simulado: boolean; error: string }>(`${CIT}/${id}/recordar`, undefined),
  eliminar:      (id: string) => http.del(`${CIT}/${id}`),
};

// ── Farmacia interna de la clínica (catálogo + recetas + POS + FEL/recibo) ───
export type ProductoFarmacia = {
  id?: string;
  codigo: string;
  nombre: string;
  categoria: 'MEDICAMENTO' | 'INSUMO' | 'PRODUCTO' | 'SERVICIO' | 'OTRO';
  precio: number;
  stock: number;
  unidad?: string | null;
  requiereReceta?: boolean;
  afectaInventario?: boolean;
  activo?: boolean;
};
export type RecetaItem = {
  id?: string;
  productoId?: string | null;
  descripcion: string;
  cantidad: number;
  indicaciones?: string | null;
};
export type Receta = {
  id?: string;
  pacienteId: string;
  medicoId?: string | null;
  consultorioId?: string | null;
  fecha?: string;
  estado?: 'EMITIDA' | 'DISPENSADA' | 'ANULADA';
  diagnostico?: string | null;
  notas?: string | null;
  items?: RecetaItem[];
};
export type VentaPosItemReq = {
  productoId?: string;
  descripcion?: string;
  cantidad: number;
  precioUnitario?: number;
};
export type CrearVentaPos = {
  pacienteId?: string;
  recetaId?: string;
  consultorioId?: string;
  medicoId?: string;
  formaPago?: 'EFECTIVO' | 'TARJETA' | 'TRANSFERENCIA' | 'CUENTA';
  notas?: string;
  quiereFactura: boolean;
  receptorNit?: string;
  receptorNombre?: string;
  items: VentaPosItemReq[];
};
export type DocumentoFiscal = {
  id?: string;
  tipo: 'FACTURA' | 'RECIBO';
  estado: 'EMITIDO' | 'CERTIFICADO' | 'ANULADO';
  serie: string;
  numero: number;
  ventaId?: string | null;
  pacienteId?: string | null;
  fecha?: string;
  receptorNit?: string | null;
  receptorNombre?: string | null;
  total: number;
  iva?: number | null;
  felUuid?: string | null;
  felSerie?: string | null;
  felNumero?: string | null;
  felAutorizacion?: string | null;
  felFecha?: string | null;
  felProveedor?: string | null;
  billingGatewayInvoiceId?: number | null;
  costoFolio?: number | null;
  precioFolio?: number | null;
};
export type VentaPos = {
  id?: string;
  pacienteId?: string | null;
  recetaId?: string | null;
  fecha?: string;
  total?: number;
  estado?: 'PAGADA' | 'PENDIENTE_COBRO' | 'ANULADA';
  formaPago?: string | null;
  documentoId?: string | null;
  notas?: string | null;
};
export type CobroPendiente = {
  id?: string;
  pacienteId: string;
  origen?: string;
  montoTotal: number;
  itemsJson?: string | null;
  vencimiento?: string | null;
  estado?: 'PENDIENTE' | 'COBRADO' | 'CANCELADO';
  notas?: string | null;
  createdAt?: string;
};
export type ResumenCobros = {
  pacienteId: string;
  ventasPendientes: VentaPos[];
  cuentasAbiertas: Cuenta[];
  cobrosPostergados: CobroPendiente[];
  montoVentasPendientes: number;
  montoCuentasAbiertas: number;
  montoPostergados: number;
  montoTotalPendiente: number;
};
export type CerrarCobroBody = {
  pacienteId: string;
  tipoDocumento: 'RECIBO' | 'FACTURA' | 'POSTERGAR';
  formaPago?: 'EFECTIVO' | 'TARJETA' | 'TRANSFERENCIA' | 'CUENTA';
  receptorNit?: string;
  receptorNombre?: string;
  cuentaId?: string;
  ventaIds?: string[];
  notas?: string;
};
export type ResultadoCierreCobro = {
  tipoDocumento: string;
  total: number;
  documento: DocumentoFiscal | null;
  ventasCobradas: VentaPos[];
  cuentaCerrada: Cuenta | null;
  cobroPostergado: CobroPendiente | null;
};
export type ConsumoFel = {
  desde: string;
  hasta: string;
  facturas: number;
  costoFolios: number;
  ingresoFolios: number;
  margen: number;
};
const FARM = '/api/facturacion/v1/facturacion/farmacia';
export const FarmaciaClinica = {
  // Catálogo
  productos:          (categoria?: string) => http.get<ProductoFarmacia[]>(`${FARM}/productos${categoria ? `?categoria=${categoria}` : ''}`),
  crearProducto:      (p: ProductoFarmacia) => http.post<ProductoFarmacia>(`${FARM}/productos`, p),
  actualizarProducto: (id: string, p: ProductoFarmacia) => http.put<ProductoFarmacia>(`${FARM}/productos/${id}`, p),
  // Recetas
  recetas:            (pacienteId?: string) => http.get<Receta[]>(`${FARM}/recetas${pacienteId ? `?pacienteId=${pacienteId}` : ''}`),
  receta:             (id: string) => http.get<Receta>(`${FARM}/recetas/${id}`),
  recetaImprimir:     (id: string, pacienteNombre?: string, pacienteDocumento?: string) => {
    const q = new URLSearchParams();
    if (pacienteNombre) q.set('pacienteNombre', pacienteNombre);
    if (pacienteDocumento) q.set('pacienteDocumento', pacienteDocumento);
    const qs = q.toString();
    return http.get<string>(`${FARM}/recetas/${id}/imprimir${qs ? `?${qs}` : ''}`);
  },
  recetaPdf:          (id: string, pacienteNombre?: string, pacienteDocumento?: string) => {
    const q = new URLSearchParams();
    if (pacienteNombre) q.set('pacienteNombre', pacienteNombre);
    if (pacienteDocumento) q.set('pacienteDocumento', pacienteDocumento);
    const qs = q.toString();
    return `${FARM}/recetas/${id}/pdf${qs ? `?${qs}` : ''}`;
  },
  crearReceta:        (r: Receta) => http.post<Receta>(`${FARM}/recetas`, r),
  anularReceta:       (id: string) => http.post<Receta>(`${FARM}/recetas/${id}/anular`, undefined),
  // Punto de venta
  ventas:             (pacienteId?: string) => http.get<VentaPos[]>(`${FARM}/ventas${pacienteId ? `?pacienteId=${pacienteId}` : ''}`),
  venta:              (id: string) => http.get<{ venta: VentaPos; items: any[] }>(`${FARM}/ventas/${id}`),
  cobrar:             (v: CrearVentaPos) => http.post<{ venta: VentaPos; items: VentaPosItemReq[]; documento: DocumentoFiscal }>(`${FARM}/ventas`, v),
  despachar:          (v: CrearVentaPos) => http.post<{ venta: VentaPos; items: VentaPosItemReq[] }>(`${FARM}/ventas/despachar`, v),
  // Documentos fiscales
  documentos:         () => http.get<DocumentoFiscal[]>(`${FARM}/documentos`),
  documento:          (id: string) => http.get<DocumentoFiscal>(`${FARM}/documentos/${id}`),
  documentoPdfUrl:    (id: string) => `${FARM}/documentos/${id}/pdf`,
  descargarPdf:       (id: string, filename?: string) =>
    downloadAuthenticatedFile(
      `${FARM}/documentos/${id}/pdf`,
      filename ?? `factura-${id}.pdf`,
    ),
  anularFel:          (id: string, motivo?: string) =>
    http.post<DocumentoFiscal>(`${FARM}/documentos/${id}/anular-fel`, motivo ? { motivo } : undefined),
  reintentarFel:      (id: string) => http.post<DocumentoFiscal>(`${FARM}/documentos/${id}/reintentar-fel`, undefined),
  consumoFel:         (desde?: string, hasta?: string) => http.get<ConsumoFel>(`${FARM}/documentos/fel/consumo${desde || hasta ? `?${desde ? `desde=${desde}` : ''}${desde && hasta ? '&' : ''}${hasta ? `hasta=${hasta}` : ''}` : ''}`),
};

const COBROS = `${FACT}/cobros`;
export const Cobros = {
  resumen:          (pacienteId: string) => http.get<ResumenCobros>(`${COBROS}/resumen?pacienteId=${pacienteId}`),
  pendientes:       () => http.get<CobroPendiente[]>(`${COBROS}/pendientes`),
  cerrar:           (body: CerrarCobroBody) => http.post<ResultadoCierreCobro>(`${COBROS}/cerrar`, body),
  cancelarPendiente:(id: string) => http.post<CobroPendiente>(`${COBROS}/pendientes/${id}/cancelar`, undefined),
};

export type BitacoraEvento = {
  id?: string; ts?: string; actor?: string|null; action: string;
  resourceType: string; resourceId?: string|null; details?: string|null;
};
export const Bitacora = {
  list: (action?: string, limit = 100) =>
    http.get<BitacoraEvento[]>(`/api/facturacion/v1/facturacion/bitacora?limit=${limit}${action ? `&action=${encodeURIComponent(action)}` : ''}`),
  registrar: (body: { action: string; resourceType: string; resourceId?: string; details?: Record<string, unknown> }) =>
    http.post<BitacoraEvento>('/api/facturacion/v1/facturacion/bitacora', body),
};

// ── Signos vitales (expediente) ──────────────────────────────────────────────
export type Signo = {
  id?: string;
  pacienteId: string;
  episodioId?: string | null;
  ta?: string | null;
  fc?: number | null;
  fr?: number | null;
  temp?: number | null;
  satO2?: number | null;
  peso?: number | null;
  talla?: number | null;
  registradoEn?: string;
};
export const Signos = {
  porPaciente: (pid: string)  => http.get<Signo[]>(`/api/expediente/v1/expediente/pacientes/${pid}/signos`),
  registrar:   (s: Signo)     => http.post<Signo>('/api/expediente/v1/expediente/signos', s),
};

// ── Episodios (admisión) ─────────────────────────────────────────────────────
export type Episodio = {
  id?: string;
  pacienteId: string;
  tipo: 'CONSULTA_EXTERNA' | 'EMERGENCIA' | 'HOSPITALIZACION';
  estado?: 'ABIERTO' | 'EN_ATENCION' | 'CERRADO' | 'ANULADO';
  numero?: string;
  motivo?: string | null;
  abiertoEn?: string;
  cerradoEn?: string | null;
  ingresoId?: string | null;
};
export const Episodios = {
  abiertos:    (tipo: Episodio['tipo']) => http.get<Episodio[]>(`/api/admision/v1/admision/episodios/abiertos?tipo=${tipo}`),
  historial:   (tipo: Episodio['tipo'], mes: string) => http.get<Episodio[]>(`/api/admision/v1/admision/episodios/historial?tipo=${tipo}&mes=${encodeURIComponent(mes)}`),
  porPaciente: (pid: string)            => http.get<Episodio[]>(`/api/admision/v1/admision/episodios/paciente/${pid}`),
  abrir:       (e: Episodio)            => http.post<Episodio>('/api/admision/v1/admision/episodios', e),
  actualizar:  (id: string, e: Partial<Episodio>) => http.put<Episodio>(`/api/admision/v1/admision/episodios/${id}`, e),
  cerrar:      (id: string)             => http.post<Episodio>(`/api/admision/v1/admision/episodios/${id}/cerrar`, undefined),
};

// ── Bodega / Compras ─────────────────────────────────────────────────────────
export type Proveedor = { id?: string; nit?: string|null; nombre: string; telefono?: string|null; email?: string|null; activo?: boolean };
export type Articulo  = { id?: string; codigo: string; nombre: string; unidad?: string|null; stockMinimo?: number; stockActual?: number; costoPromedio?: number; activo?: boolean };
export type MovimientoInventario = { id?: string; articuloId: string; tipo: 'ENTRADA'|'SALIDA'|'AJUSTE'|'MERMA'|'TRASLADO'; cantidad: number; costoUnitario?: number|null; referencia?: string|null; motivo?: string|null; };
export type OrdenCompra = { id?: string; numero?: string; proveedorId: string; estado?: string; subtotal?: number; iva?: number; total?: number; fecha?: string };
export const Bodega = {
  proveedores: ()                      => http.get<Proveedor[]>('/api/bodega/v1/bodega/proveedores'),
  crearProv:   (p: Proveedor)          => http.post<Proveedor>('/api/bodega/v1/bodega/proveedores', p),
  articulos:   (bajoMinimo = false)    => http.get<Articulo[]>(`/api/bodega/v1/bodega/articulos${bajoMinimo ? '?bajoMinimo=true' : ''}`),
  crearArt:    (a: Articulo)           => http.post<Articulo>('/api/bodega/v1/bodega/articulos', a),
  mover:       (m: MovimientoInventario) => http.post<MovimientoInventario>('/api/bodega/v1/bodega/movimientos', m),
  ordenes:     ()                      => http.get<OrdenCompra[]>('/api/bodega/v1/bodega/ordenes'),
  crearOrden:  (o: any)                => http.post<OrdenCompra>('/api/bodega/v1/bodega/ordenes', o),
};

// ── Tesorería ────────────────────────────────────────────────────────────────
export type CuentaBancaria = { id?: string; banco: string; numero: string; alias?: string|null; tipo: 'MONETARIA'|'AHORRO'|'PLAZO'; moneda?: string; saldoActual?: number; activa?: boolean };
export type TransaccionBancaria = { id?: string; cuentaId: string; tipo: string; monto: number; referencia?: string|null; concepto?: string|null; fecha?: string; conciliada?: boolean };
export type Cheque = { id?: string; cuentaId: string; numero: string; beneficiario: string; monto: number; concepto?: string|null; estado?: string; fechaEmision?: string };
export const Tesoreria = {
  cuentas:    ()                      => http.get<CuentaBancaria[]>('/api/tesoreria/v1/tesoreria/cuentas'),
  crearCta:   (c: CuentaBancaria)     => http.post<CuentaBancaria>('/api/tesoreria/v1/tesoreria/cuentas', c),
  transac:    (cuentaId: string)      => http.get<TransaccionBancaria[]>(`/api/tesoreria/v1/tesoreria/transacciones?cuentaId=${cuentaId}`),
  crearTx:    (t: TransaccionBancaria) => http.post<TransaccionBancaria>('/api/tesoreria/v1/tesoreria/transacciones', t),
  conciliar:  (id: string)            => http.post<TransaccionBancaria>(`/api/tesoreria/v1/tesoreria/transacciones/${id}/conciliar`, undefined),
  cheques:    ()                      => http.get<Cheque[]>('/api/tesoreria/v1/tesoreria/cheques'),
  emitir:     (c: Cheque)             => http.post<Cheque>('/api/tesoreria/v1/tesoreria/cheques', c),
  cobrarChq:  (id: string)            => http.post<Cheque>(`/api/tesoreria/v1/tesoreria/cheques/${id}/cobrar`, undefined),
};

// ── Nómina ───────────────────────────────────────────────────────────────────
export type Empleado = { id?: string; codigo: string; cui?: string|null; nit?: string|null; nombres: string; apellidos: string; puesto?: string|null; departamento?: string|null; salarioBase: number; bonificacion?: number; fechaIngreso?: string; activo?: boolean };
export type Planilla = { id?: string; numero?: string; tipo: 'QUINCENA'|'FIN_DE_MES'|'BONO_14'|'AGUINALDO'|'EXTRA'; periodo: string; estado?: string; totalBruto?: number; totalNeto?: number };
export const Nomina = {
  empleados:    ()                       => http.get<Empleado[]>('/api/nomina/v1/nomina/empleados'),
  crearEmp:     (e: Empleado)            => http.post<Empleado>('/api/nomina/v1/nomina/empleados', e),
  planillas:    ()                       => http.get<Planilla[]>('/api/nomina/v1/nomina/planillas'),
  crearPlan:    (p: Planilla)            => http.post<Planilla>('/api/nomina/v1/nomina/planillas', p),
  calcular:     (id: string)             => http.post<Planilla>(`/api/nomina/v1/nomina/planillas/${id}/calcular`, undefined),
  autorizar:    (id: string)             => http.post<Planilla>(`/api/nomina/v1/nomina/planillas/${id}/autorizar`, undefined),
  pagar:        (id: string)             => http.post<Planilla>(`/api/nomina/v1/nomina/planillas/${id}/pagar`, undefined),
  detalle:      (id: string)             => http.get<any[]>(`/api/nomina/v1/nomina/planillas/${id}/detalle`),
};

// ── Contabilidad ─────────────────────────────────────────────────────────────
export type CuentaContable = { id?: string; codigo: string; nombre: string; tipo: 'ACTIVO'|'PASIVO'|'PATRIMONIO'|'INGRESO'|'GASTO'|'COSTO'|'ORDEN'; naturaleza: 'DEUDORA'|'ACREEDORA'; padreCodigo?: string|null; nivel?: number; permiteMov?: boolean; saldoActual?: number };
export type AsientoContable = { id?: string; numero?: string; fecha: string; concepto: string; estado?: string; totalDebe?: number; totalHaber?: number; partidas: Array<{ cuentaId: string; debe: number; haber: number; descripcion?: string }> };
export const Contabilidad = {
  cuentas:    (tipo?: string)            => http.get<CuentaContable[]>(`/api/contabilidad/v1/contabilidad/cuentas${tipo ? '?tipo=' + tipo : ''}`),
  crearCta:   (c: CuentaContable)        => http.post<CuentaContable>('/api/contabilidad/v1/contabilidad/cuentas', c),
  asientos:   (desde?: string, hasta?: string) => http.get<AsientoContable[]>(`/api/contabilidad/v1/contabilidad/asientos${desde && hasta ? `?desde=${desde}&hasta=${hasta}` : ''}`),
  crearAs:    (a: AsientoContable)       => http.post<AsientoContable>('/api/contabilidad/v1/contabilidad/asientos', a),
  registrar:  (id: string)               => http.post<AsientoContable>(`/api/contabilidad/v1/contabilidad/asientos/${id}/registrar`, undefined),
  balance:    ()                         => http.get<any>('/api/contabilidad/v1/contabilidad/balance'),
  estadoResultados: ()                   => http.get<any>('/api/contabilidad/v1/contabilidad/estado-resultados'),
};

// ── Reportes MSPAS ───────────────────────────────────────────────────────────
export const Reportes = {
  egresos:        ()              => http.get<any[]>('/api/reportes-mspas/v1/reportes/egresos'),
  crearEgreso:    (e: any)        => http.post<any>('/api/reportes-mspas/v1/reportes/egresos', e),
  defunciones:    ()              => http.get<any[]>('/api/reportes-mspas/v1/reportes/defunciones'),
  crearDefuncion: (d: any)        => http.post<any>('/api/reportes-mspas/v1/reportes/defunciones', d),
  infecciones:    ()              => http.get<any[]>('/api/reportes-mspas/v1/reportes/infecciones'),
  crearInfeccion: (i: any)        => http.post<any>('/api/reportes-mspas/v1/reportes/infecciones', i),
  indicadores:    (desde?: string, hasta?: string) => http.get<any>(`/api/reportes-mspas/v1/reportes/indicadores${desde && hasta ? `?desde=${desde}&hasta=${hasta}` : ''}`),
};

// ── Gestión de usuarios del tenant (admin-tenant) ────────────────────────────
export type TenantUser = {
  id: string;
  username: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  enabled: boolean;
  roles: string[];
};
const TUSR = '/api/auth/v1/tenant';
export const Usuarios = {
  rolesAsignables: () => http.get<{ roles: string[] }>(`${TUSR}/roles`),
  list:            () => http.get<{ users: TenantUser[] }>(`${TUSR}/users`),
  crear:           (body: { usuario: string; nombre?: string; apellido?: string; email?: string; roles: string[] }) =>
                      http.post<{ id: string; usuario: string; tempPassword: string; roles: string[] }>(`${TUSR}/users`, body),
  setRoles:        (userId: string, roles: string[]) => http.put<{ ok: boolean }>(`${TUSR}/users/${userId}/roles`, { roles }),
  setEnabled:      (userId: string, enabled: boolean) => http.post<{ ok: boolean }>(`${TUSR}/users/${userId}/enabled`, { enabled }),
  resetPassword:   (userId: string) => http.post<{ tempPassword: string }>(`${TUSR}/users/${userId}/reset-password`, undefined),
};

export type TenantBrandingConfig = {
  logoUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  nombreComercial?: string | null;
  dominio?: string | null;
};

export const TenantBranding = {
  obtener: () => http.get<{ branding: TenantBrandingConfig; nombre: string }>(`${TUSR}/branding`),
  actualizar: (body: Partial<TenantBrandingConfig>) =>
    http.patch<{ branding: TenantBrandingConfig }>(`${TUSR}/branding`, body),
};

export type TenantWhatsAppConfig = {
  enabled: boolean;
  numero: string;
  metaConectado: boolean;
  estado: string;
};

export const TenantWhatsAppApi = {
  obtener: () => http.get<TenantWhatsAppConfig>(`${TUSR}/whatsapp`),
  actualizar: (body: { enabled: boolean; numero: string }) =>
    http.patch<TenantWhatsAppConfig>(`${TUSR}/whatsapp`, body),
};

// ── Dashboard agregado (asistencial-ms) ─────────────────────────────────────
export type DashboardResumen = {
  tenantId: string;
  generadoEn: string;
  semaforo: {
    totalCamas: number;
    ocupadas: number;
    disponibles: number;
    tasaOcupacionPct: number;
    porColor: Record<string, number>;
  };
  ingresosActivos: number;
  ingresosRecientes: Array<{
    id: string;
    pacienteId: string;
    pacienteNombre: string;
    camaId?: string;
    camaNumero?: string;
    servicio?: string;
    diagnosticoIngreso?: string;
    ingresadoEn?: string;
  }>;
  camas: Cama[];
  kardexPendientes: number;
  cirugiasHoy: number;
  camasMantenimiento?: number;
  urgenciasActivas?: number;
  cirugias: Array<{
    id: string;
    procedimiento: string;
    estado: string;
    fechaProgramada: string;
    cirujanoId?: string;
  }>;
};

export const Dashboard = {
  resumen: () => http.get<DashboardResumen>('/api/camas/v1/camas/dashboard/resumen'),
};

// ── Hospital extras (clínicos) ───────────────────────────────────────────────
export const Hospital = {
  semaforo:        ()             => http.get<any>('/api/camas/v1/camas/semaforo'),
  traspasos:       (camaId?: string) => http.get<any[]>(`/api/camas/v1/camas/traspasos${camaId ? '?camaId=' + camaId : ''}`),
  crearTraspaso:   (t: any)       => http.post<any>('/api/camas/v1/camas/traspasos', t),
  news2:           (pid: string)  => http.get<any>(`/api/expediente/v1/expediente/pacientes/${pid}/news2`),
  indicaciones:    (pid: string, soloActivas = true) => http.get<any[]>(`/api/expediente/v1/expediente/pacientes/${pid}/indicaciones?soloActivas=${soloActivas}`),
  crearIndicacion: (i: any)       => http.post<any>('/api/expediente/v1/expediente/indicaciones', i),
  kardexPaciente:  (pid: string)  => http.get<any[]>(`/api/farmacia-hosp/v1/farmacia-hosp/kardex/paciente/${pid}`),
  kardexPendientes:()             => http.get<any[]>('/api/farmacia-hosp/v1/farmacia-hosp/kardex/pendientes'),
  programarAdmin:  (a: any)       => http.post<any>('/api/farmacia-hosp/v1/farmacia-hosp/kardex/programar', a),
  administrar:     (id: string, who: string) => http.post<any>(`/api/farmacia-hosp/v1/farmacia-hosp/kardex/${id}/administrar?administradaPor=${encodeURIComponent(who)}`, undefined),
  omitir:          (id: string, motivo: string) => http.post<any>(`/api/farmacia-hosp/v1/farmacia-hosp/kardex/${id}/omitir?motivo=${encodeURIComponent(motivo)}`, undefined),
  altaFirmar:      (a: any)       => http.post<any>('/api/admision/v1/admision/altas', a),
  altaPorEpisodio: (id: string)   => http.get<any>(`/api/admision/v1/admision/altas/episodio/${id}`),
  altaImprimibleUrl: (id: string) => `/api/admision/v1/admision/altas/episodio/${id}/imprimible`,
};
